import { memo, PropsWithChildren, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, Text, Truncated } from '@frontend/design-system';
import { CALENDAR_Z_INDICES } from '../../constants';
import { useCalendarGridStore, useNonReactiveStore } from '../../store/calendar-grid.store';
import { CalendarColumnGroup, CalendarColumnHeaderType, ColumnHeaderOverlayStyleFn } from '../../types';
import { CalendarDragStateContext, defaultCalendarDragState } from '../calendar-drag-state-context';
import { listItemSideSeparatorStyle } from '../styles';
import { ColumnHeader } from './column-header';
import { SortableColumnHeader } from './sortable-column-header';

const MIN_DRAG_DISTANCE = 5;

interface Props {
  groupInfo: CalendarColumnGroup;
  gridColumn: string;
  isGroupHeaderVisible: boolean;
  headerType: CalendarColumnHeaderType;
  columnIds: string[];
}

interface GroupHeaderContainerProps extends Props {
  renderColumn: (columnId: string) => React.ReactNode;
}

const GroupHeaderContainer = ({
  groupInfo,
  gridColumn,
  isGroupHeaderVisible,
  columnIds,
  renderColumn,
}: GroupHeaderContainerProps) => {
  const groupNameSpan = isGroupHeaderVisible && (
    <div css={groupNameContainerStyle} style={{ gridColumn: `1 / span ${Math.max(columnIds.length, 1)}` }}>
      <div>
        <div>
          <Icon name='location' color='subdued' size={16} />
          <Truncated as={Text} color='subdued' size='small' weight='bold'>
            {groupInfo.name}
          </Truncated>
        </div>
        {!!groupInfo.timezone && <Chip.TimeZone>{groupInfo.timezone}</Chip.TimeZone>}
      </div>
    </div>
  );

  return (
    <div css={containerStyle} style={{ gridColumn }}>
      {groupNameSpan}
      {columnIds.map((columnId) => renderColumn(columnId))}
    </div>
  );
};

export const GroupHeader = (props: Props) => {
  const { headerType, groupInfo, columnIds } = props;
  const isColumnReorderEnabled = useCalendarGridStore((s) => s.isColumnReorderEnabled);

  if (!isColumnReorderEnabled) {
    return (
      <GroupHeaderContainer
        {...props}
        renderColumn={(columnId) => (
          <ColumnHeader key={columnId} columnId={columnId} headerType={headerType} groupId={groupInfo.id} />
        )}
      />
    );
  }

  return (
    <GroupHeaderDndWrapper {...props}>
      <GroupHeaderContainer
        {...props}
        renderColumn={(columnId) => (
          <SortableColumnHeader
            key={columnId}
            columnId={columnId}
            headerType={headerType}
            groupId={groupInfo.id}
            columnIds={columnIds}
          />
        )}
      />
    </GroupHeaderDndWrapper>
  );
};

const GroupHeaderDndWrapperComponent = ({ groupInfo, columnIds, children }: PropsWithChildren<Props>) => {
  const { setLocalColumnOrder, clearLocalColumnOrder, setIsColumnReorderDragging } = useCalendarGridStore([
    'setLocalColumnOrder',
    'clearLocalColumnOrder',
    'setIsColumnReorderDragging',
  ]);
  const headerTypeFromStore = useCalendarGridStore((s) => s.headerType);
  const nonReactiveStore = useNonReactiveStore();

  const [activeDragColumnId, setActiveDragColumnId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: MIN_DRAG_DISTANCE } }));

  const groupId = groupInfo.id;

  const handleDragStart = ({ active }: DragStartEvent) => {
    const columnId = active.id as string;
    const { localColumnOrderByGroup, groupsById } = nonReactiveStore.getState();
    const currentOrder = localColumnOrderByGroup[groupId] ?? groupsById[groupId]?.columnIds ?? [];
    setActiveDragColumnId(columnId);
    setIsColumnReorderDragging(true);
    setLocalColumnOrder(groupId, [...currentOrder]);
  };

  const handleDragOver = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const { localColumnOrderByGroup, groupsById, columnsById } = nonReactiveStore.getState();
    // unassigned columns keep their slot, so they can never be displaced by a drag.
    if (
      columnsById[over.id as string]?.[groupId]?.type === 'unassigned' ||
      columnsById[active.id as string]?.[groupId]?.type === 'unassigned'
    )
      return;
    const currentOrder = localColumnOrderByGroup[groupId] ?? groupsById[groupId]?.columnIds ?? [];
    const oldIndex = currentOrder.indexOf(active.id as string);
    const newIndex = currentOrder.indexOf(over.id as string);
    if (oldIndex !== -1 && newIndex !== -1) {
      setLocalColumnOrder(groupId, arrayMove(currentOrder, oldIndex, newIndex));
    }
  };

  const handleDragEnd = () => {
    setActiveDragColumnId(null);
    const { localColumnOrderByGroup, groupsById, onColumnReorder } = nonReactiveStore.getState();
    const localOrder = localColumnOrderByGroup[groupId];
    const originalOrder = groupsById[groupId]?.columnIds ?? [];

    if (localOrder && localOrder.join(',') !== originalOrder.join(',')) {
      onColumnReorder?.(groupId, localOrder);
    }

    // Do NOT clear localColumnOrderByGroup or re-enable scroll snap immediately.
    // The consumer's onColumnReorder callback triggers state updates that propagate
    // through CalendarGridProvider → CalendarGridStoreSync (a useEffect, so post-paint).
    // If we clear and re-enable in this same cycle, scrollSnapType fires on the OLD layout.
    // Two rAFs ensure we wait past: render cycle (1st rAF) + useEffect post-paint (2nd rAF).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        clearLocalColumnOrder(groupId);
        setIsColumnReorderDragging(false);
      });
    });
  };

  const handleDragCancel = () => {
    clearLocalColumnOrder(groupId);
    setActiveDragColumnId(null);
    setIsColumnReorderDragging(false);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      autoScroll={{
        threshold: {
          x: 0.2, // Default or desired value for horizontal auto-scroll
          y: 0, // Set to 0 to block vertical auto-scroll
        },
        layoutShiftCompensation: false,
      }}
    >
      <SortableContext id={groupId} items={columnIds} strategy={horizontalListSortingStrategy}>
        {children}
      </SortableContext>
      {createPortal(
        <ColumnHeaderDragOverlay
          columnId={activeDragColumnId ?? ''}
          groupId={groupId}
          headerType={headerTypeFromStore}
        />,
        document.body
      )}
    </DndContext>
  );
};

const ColumnHeaderDragOverlay = ({
  columnId,
  groupId,
  headerType,
}: {
  columnId: string;
  groupId: string;
  headerType: CalendarColumnHeaderType;
}) => {
  const dragStyles = useCalendarGridStore((s) => s.dragStyles);
  const columnHeaderDragOverlayStyle =
    typeof dragStyles.columnHeaderDragOverlay === 'function'
      ? (dragStyles.columnHeaderDragOverlay as ColumnHeaderOverlayStyleFn)(columnId, groupId)
      : dragStyles.columnHeaderDragOverlay;

  return (
    <DragOverlay dropAnimation={null}>
      {columnId && (
        <div css={[defaultColumnHeaderDragOverlayStyle]}>
          <div css={overlayDragHandleStyle}>
            <Icon name='dragand-drop-handle' size={20} color='subdued' />
          </div>
          <CalendarDragStateContext.Provider
            value={{ ...defaultCalendarDragState, isColumnHeaderDragOverlay: true, columnHeaderDragOverlayStyle }}
          >
            <div css={overlayContentStyle}>
              <ColumnHeader columnId={columnId} headerType={headerType} groupId={groupId} />
            </div>
          </CalendarDragStateContext.Provider>
        </div>
      )}
    </DragOverlay>
  );
};

const GroupHeaderDndWrapper = memo(GroupHeaderDndWrapperComponent);

// Mirrors SortableColumnHeader's dragHandleStyle inside the drag overlay
const overlayDragHandleStyle = css({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  cursor: 'grabbing',
  paddingLeft: theme.spacing(0.5),
});

// Mirrors SortableColumnHeader's contentStyle inside the drag overlay
const overlayContentStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 0,
  flex: 1,
  padding: theme.spacing(0, 2.5),
  width: '100%',
});

/**
 * Default drag overlay styles matching the existing timeline column header implementation:
 * rotate -10deg, floating shadow, white background, rounded corners, blue left border.
 * Consumers can override via dragStyles.columnHeaderDragOverlay.
 */
const defaultColumnHeaderDragOverlayStyle = css({
  display: 'flex',
  alignItems: 'center',
  transform: 'rotate(-10deg)',
  boxShadow: theme.shadows.floating,
  borderRadius: theme.borderRadius.medium,
  backgroundColor: theme.colors.white,
  padding: theme.spacing(0.5),
  borderLeft: `4px solid ${theme.colors.primary50}`,
  height: 'auto',
  cursor: 'grabbing',
});

const containerStyle = css({
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridRow: 1,
  position: 'sticky',
  top: 0,
  zIndex: CALENDAR_Z_INDICES.groupHeader,
  boxShadow: theme.shadows.light,
  backgroundColor: theme.colors.white,
  '> [role="button"]': listItemSideSeparatorStyle,
});

const groupNameContainerStyle = css({
  display: 'flex',
  justifyContent: 'center',
  borderBottom: `1px solid ${theme.colors.neutral20}`,
  '> div': {
    padding: theme.spacing(0.5),
    backgroundColor: theme.colors.neutral5,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    maxWidth: 'calc(var(--viewport-width) - var(--time-column-width))',
    width: '100%',
    textAlign: 'center',
    position: 'sticky',
    left: 'var(--time-column-width)',
    right: 0,
    '> div:first-child': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      minWidth: '0',
    },
  },
});
